import { memo } from 'react'

import TouchDeviceContext from '../../contexts/TouchDeviceContext'
import { SearchQueryHookResult } from '../../hooks/useQueries'

import AdventureCard from '../../components/cards/AdventureCard'
import ListingCard from '../../components/cards/ListingCard'
import StoryCard from '../../components/cards/StoryCard'
import WithSelectEscape from '../../components/SelectEscape/WithSelectEscape'
import PlaceholderCard from '../../components/cards/PlaceholderCard'
import RenderOnlyWhenVisible from '../../components/utils/RenderOnlyWhenVisible'
import { GridItem } from '../../components/layout/ItemGrid'
import { formatUrlWithFilters } from '../utils'

type SearchNode = SearchQueryHookResult['data']['search']['results']['edges'][0]['node']

type ResultProps = {
  result: SearchNode['result']
  pricing: SearchNode['pricing']
  discount: SearchNode['discount']
  availableDates?: { startDate: string; endDate: string; total: number }[]
  filters: GQL_SearchFilters
  isMobile: boolean
  bookingAdventuresIndex: GQL_BookingAdventuresIndex
  bucketListItemsIndex: GQL_BucketListItemsIndex
}

const FullResult = memo(
  ({
    result,
    pricing,
    discount,
    availableDates,
    filters,
    isMobile,
    bookingAdventuresIndex,
    bucketListItemsIndex,
  }: ResultProps) => {
    // Filters get serialized to the URL so the booking form can be prepopulated with dates and guests.
    // If the search was filtered by weekend availability rather than specific dates, the start and end
    // dates need to be set from the weekend availability data.
    const filtersToSerializeToUrl = {
      ...filters,
      ...(availableDates?.[0]?.startDate != null ? availableDates[0] : {}),
    }

    return (
      <TouchDeviceContext.Consumer>
        {(isTouchDevice) => {
          switch (result.__typename) {
            case 'Story':
              return (
                <WithSelectEscape
                  itemId={result.id}
                  itemType="Story"
                  bucketListItemsIndex={bucketListItemsIndex}
                  analyticsSource="search"
                >
                  <StoryCard
                    story={{
                      ...result,
                      url: formatUrlWithFilters(
                        filtersToSerializeToUrl,
                        result.url
                      ),
                    }}
                    listing={{
                      ...result.featuredListing,
                      pricing,
                      discount,
                    }}
                    asLink={isTouchDevice || isMobile}
                    availableDates={availableDates}
                  />
                </WithSelectEscape>
              )

            case 'Listing':
              return (
                <WithSelectEscape
                  itemId={result.id}
                  itemType="Listing"
                  bucketListItemsIndex={bucketListItemsIndex}
                  analyticsSource="search"
                >
                  <ListingCard
                    listing={{
                      ...result,
                      pricing,
                      url: formatUrlWithFilters(
                        filtersToSerializeToUrl,
                        result.url
                      ),
                      discount,
                    }}
                    asLink={isTouchDevice || isMobile}
                    availableDates={availableDates}
                  />
                </WithSelectEscape>
              )

            case 'StoryAdventure':
              return (
                <WithSelectEscape
                  itemId={result.id}
                  itemType="Adventure"
                  bookingAdventuresIndex={bookingAdventuresIndex}
                  bucketListItemsIndex={bucketListItemsIndex}
                  analyticsSource="search"
                >
                  <AdventureCard
                    {...result}
                    asLink={isTouchDevice || isMobile}
                    showReviewScore
                  />
                </WithSelectEscape>
              )

            default:
              return null
          }
        }}
      </TouchDeviceContext.Consumer>
    )
  }
)

const Result = (props: ResultProps) => {
  const { result } = props

  return (
    <>
      <GridItem key={`${result.__typename}-${result.id}`}>
        <RenderOnlyWhenVisible
          rootMargin="50% 0px"
          placeholder={
            <PlaceholderCard portrait={result.__typename === 'Story'} />
          }
        >
          <FullResult {...props} />
        </RenderOnlyWhenVisible>
      </GridItem>
    </>
  )
}

export default Result
